/**
 * @fileoverview Settings context for managing user preferences.
 * @module contexts/SettingsContext
 */

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { STORAGE_KEYS, THEME_CONFIG, TYPOGRAPHY } from '@/constants';
import { useLocalStorage } from '@/hooks';
import type { Theme, UserSettings } from '@/types';

// ============================================================================
// Context Definition
// ============================================================================

interface SettingsContextValue extends UserSettings {
  /** Updates the color theme */
  setTheme: (theme: Theme) => void;
  /** Updates the font size */
  setFontSize: (size: number) => void;
  /** Resets all settings to defaults */
  resetSettings: () => void;
  /** Available themes */
  themes: typeof THEME_CONFIG;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface SettingsProviderProps {
  children: ReactNode;
}

/**
 * Provides settings context to the application.
 *
 * @example
 * ```tsx
 * <SettingsProvider>
 *   <App />
 * </SettingsProvider>
 * ```
 */
export function SettingsProvider({ children }: SettingsProviderProps) {
  // User requested sepia as default
  const defaultTheme: Theme = 'sepia';

  const [theme, setThemeValue] = useLocalStorage<Theme>(STORAGE_KEYS.THEME, defaultTheme);

  const [fontSize, setFontSizeValue] = useLocalStorage<number>(
    STORAGE_KEYS.FONT_SIZE,
    TYPOGRAPHY.fontSize.default
  );

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Remove all theme classes
    Object.keys(THEME_CONFIG).forEach(t => {
      root.classList.remove(`theme-${t}`);
      body.classList.remove(`theme-${t}`);
    });

    // Add current theme class
    root.classList.add(`theme-${theme}`);
    body.classList.add(`theme-${theme}`);
    root.setAttribute('data-theme', theme);
  }, [theme]);

  // Apply font size to CSS variable
  useEffect(() => {
    document.documentElement.style.setProperty('--reader-font-size', `${fontSize}px`);
  }, [fontSize]);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      if (Object.keys(THEME_CONFIG).includes(newTheme)) {
        setThemeValue(newTheme);
      }
    },
    [setThemeValue]
  );

  const setFontSize = useCallback(
    (size: number) => {
      const clampedSize = Math.min(
        TYPOGRAPHY.fontSize.max,
        Math.max(TYPOGRAPHY.fontSize.min, size)
      );
      setFontSizeValue(clampedSize);
    },
    [setFontSizeValue]
  );

  const resetSettings = useCallback(() => {
    setThemeValue(defaultTheme);
    setFontSizeValue(TYPOGRAPHY.fontSize.default);
  }, [setThemeValue, setFontSizeValue, defaultTheme]);

  const value = useMemo<SettingsContextValue>(
    () => ({
      theme,
      fontSize,
      language: 'both',
      setTheme,
      setFontSize,
      resetSettings,
      themes: THEME_CONFIG,
    }),
    [theme, fontSize, setTheme, setFontSize, resetSettings]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access settings context.
 *
 * @throws Error if used outside of SettingsProvider
 *
 * @example
 * ```tsx
 * const { theme, setTheme, fontSize, setFontSize } = useSettings();
 * ```
 */
export function useSettings(): SettingsContextValue {
  const context = useContext(SettingsContext);

  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }

  return context;
}
